import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '@solcre-org/core';
import {
  ColumnConfig,
  GenericTableComponent,
  GlobalAction,
  ModalMode,
  ModalTabConfig,
  TableActionConfig,
} from '@solcre-org/core-ui';
import { environment } from '../../../../../../../environments/environment.local';
import { QuoteModel } from '../../../../../../shared/models/quote.model';
import { ClientQuotesResponseModel } from '../../../../../../shared/models/clientQuotes.model';

@Component({
  selector: 'app-quotes',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './quotes.html',
})
export class Quotes {
  private route = inject(ActivatedRoute);
  private apiService = inject(ApiService);
  private hasUserInteracted = signal<boolean>(false);

  fieldErrors = signal<Map<string, string[]>>(new Map());
  showValidationErrors = signal<boolean>(false);
  modalMode = signal<ModalMode>(ModalMode.EDIT);
  editedData = signal<any | null>(null);
  clientId = signal<string | null>(null);
  quotesData = signal<QuoteModel[]>([]);
  quotes = signal<QuoteModel[]>([]);
  globalActions: GlobalAction<QuoteModel>[] = [];
  endpoint = computed<string>(() => {
    return environment.API.CLIENTS_CONTACTS_URI;
  });
  customParams = computed<Record<string, any>>(() => {
    return {
      client: this.clientId(),
    };
  });

  columns: ColumnConfig<QuoteModel>[] = [
    {
      label: 'quotes.id',
      key: 'quoteNumber',
    },
    {
      label: 'quotes.client',
      key: 'clientComercialName',
    },
    {
      label: 'quotes.portLoading',
      key: 'portLoading',
      format: (value: any, row: QuoteModel) => row.polPort ? (row.polPort.terminal ? `${row.polPort.fullName} - ${row.polPort.terminal}` : (row.polPort.fullName || '')) : '',
    },
    {
      label: 'quotes.portDischarge',
      key: 'portDischarge',
      format: (value: any, row: QuoteModel) => row.podPort ? (row.podPort.terminal ? `${row.podPort.fullName} - ${row.podPort.terminal}` : (row.podPort.fullName || '')) : '',
    },
    {
      label: 'quotes.status',
      key: 'status',
    },
    {
      label: 'quotes.amount',
      key: 'amount',
      format: (value: string, row: QuoteModel) =>
        `${row.amount} ${row.currency}`,
    },
    {
      label: 'quotes.expirationDate',
      key: 'expirationDate',
      format: (value: any) => new Date(value).toLocaleString(),
    },
  ];

  modalFields = computed<ModalTabConfig<QuoteModel>[]>(() => []);

  actions: TableActionConfig[] = [];

  ngOnInit() {
    this.clientId.set(this.route.snapshot.paramMap.get('id'));
    this.loadQuotesData();
  }

  private loadQuotesData(): void {
    const endpoint = this.endpoint();
    if (!endpoint) return;

    this.apiService.fetchData(endpoint).subscribe({
      next: (response: any) => {
        const clientContactsResponse = new ClientQuotesResponseModel().fromJSON(
          response.data?.data ?? []
        );
        this.quotesData.set(clientContactsResponse.quotes);
      },
      error: (error) => {
        this.quotesData.set([]);
      },
    });
  }

  onFieldValueChange(key: string, value: any): void {
    this.hasUserInteracted.set(true);
  }

  getFieldErrors(key: string): string[] {
    if (!this.showValidationErrors()) {
      return [];
    }
    return this.fieldErrors().get(key) || [];
  }

  quotesModelFactory = (data: Partial<QuoteModel>): QuoteModel => {
    return new QuoteModel().fromJSON(data);
  };
}
